import { FC, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'

import {
	Box,
	Badge,
	Button,
	Container,
	Form,
	FormField,
	Header,
	Input,
	Modal,
	SpaceBetween
} from '@cloudscape-design/components'

import LoadingSpinner from '../../../components/LoadingSpinner'
import RaciRoleSelector from '../../../components/AccessManagement/RaciRoleSelector'
import { showAccessManagementNotification } from '../../../helper/accessManagementFlashbar'
import { useFlashbarStateStore } from '../../../stores/notifications/flashbarStateStore'

import useDeletePermissionsForUser from '../../../hooks/mutate/useDeletePermissionsForUser'
import useUpdatePermissionsForUser from '../../../hooks/mutate/useUpdatePermissionsForUser'
import useUserPermissions from '../../../hooks/query/useUserPermissions'
import { useUpdateAccessManagementStore } from '../../../stores/access-management/updateAccessManagementFormStore'
import type {
	DeleteAccessRequest,
	UpdateAccessRequest
} from '../../../types/access'
import hasRequiredPermissions from '../../../helper/hasRequiredPermissions'
import {
	buildPermissionsPayload,
	detectRaciRolesFromPermissions,
	getUnmappedPermissionsForEdit,
	isDefaultBootstrapPermission,
	isSuperAdmin,
	isSystemManagedPermission,
	normalizeRaciRoleSelection,
	toCustomPermissionTokens
} from '../../../helper/permissionRoles'

const AccessManagementDetails: FC = () => {
	const { userEmail } = useParams() as { userEmail: string }
	const navigate = useNavigate()
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const {
		formValues,
		actions: { setFormValues, setFormValidity, clearFormValues }
	} = useUpdateAccessManagementStore()

	const {
		control,
		trigger,
		reset,
		formState: { isValid }
	} = useForm<{
		raciRoles: Array<{ label: string; value: string }>
		customPermissions: Array<{ label: string; dismissLabel: string }>
	}>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: {
			raciRoles: [],
			customPermissions: []
		}
	})

	useEffect(() => {
		setFormValidity(isValid)
	}, [isValid, setFormValidity])

	const { data: access, isLoading } = useUserPermissions(userEmail)
	const { data: currentUserAccess } = useUserPermissions(thisUserEmail)

	const canModifyAccess = useMemo(
		() =>
			hasRequiredPermissions(
				['useraccess:*:update'],
				currentUserAccess?.permissions || []
			),
		[currentUserAccess?.permissions]
	)

	const canDeleteAccess = useMemo(
		() =>
			hasRequiredPermissions(
				['useraccess:*:delete'],
				currentUserAccess?.permissions || []
			),
		[currentUserAccess?.permissions]
	)

	const { mutate: updatePermissionsForUser, isLoading: updateLoading } =
		useUpdatePermissionsForUser(userEmail)

	const { mutate: deletePermissionsForUser, isLoading: deleteLoading } =
		useDeletePermissionsForUser(userEmail)

	const derivedFormValues = useMemo(() => {
		if (!access?.permissions) {
			return null
		}

		const detectedRoles = detectRaciRolesFromPermissions(access.permissions)
		const unmappedPermissions = getUnmappedPermissionsForEdit(
			access.permissions,
			detectedRoles
		)

		return {
			raciRoles: normalizeRaciRoleSelection(detectedRoles),
			customPermissions: toCustomPermissionTokens(unmappedPermissions)
		}
	}, [access])

	const [isFormHydrated, setIsFormHydrated] = useState(false)
	const [deleteModalVisible, setDeleteModalVisible] = useState(false)
	const [saveModalVisible, setSaveModalVisible] = useState(false)
	const flashbarActions = useFlashbarStateStore()

	useEffect(() => {
		return () => {
			clearFormValues()
			setIsFormHydrated(false)
		}
	}, [clearFormValues])

	useLayoutEffect(() => {
		if (!derivedFormValues) {
			setIsFormHydrated(false)
			return
		}

		reset(derivedFormValues)
		setFormValues('raciRoles', derivedFormValues.raciRoles)
		setFormValues('customPermissions', derivedFormValues.customPermissions)
		setIsFormHydrated(true)
	}, [derivedFormValues, reset, setFormValues])

	const handleSaveClick = async () => {
		const valid = await trigger()
		if (!valid || !access) return
		setSaveModalVisible(true)
	}

	const handleAccessChange = async () => {
		if (!access) return

		const systemPermissions = access.permissions.filter(isSystemManagedPermission)
		const bootstrapPermissions = access.permissions.filter(
			isDefaultBootstrapPermission
		)
		const permissions = Array.from(
			new Set([
				...buildPermissionsPayload(
					formValues.raciRoles,
					formValues.customPermissions
				),
				...bootstrapPermissions,
				...systemPermissions
			])
		)

		const updateAccess: UpdateAccessRequest = {
			userEmail: userEmail,
			permissions,
			action: 'UPDATE'
		}

		updatePermissionsForUser(updateAccess, {
			onSuccess: () => {
				setSaveModalVisible(false)
				showAccessManagementNotification(flashbarActions, {
					type: 'success',
					header: 'Access updated',
					content: `Access for ${userEmail} was updated successfully.`
				})
				navigate('/access-management')
			}
		})
	}

	const handleAccessDelete = () => {
		const deleteAccessRequestPayload: DeleteAccessRequest = {
			userEmail,
			action: 'DELETE'
		}

		deletePermissionsForUser(deleteAccessRequestPayload, {
			onSuccess: () => {
				setDeleteModalVisible(false)
				showAccessManagementNotification(flashbarActions, {
					type: 'success',
					header: 'Access deleted',
					content: `All SSP access for ${userEmail} was removed.`
				})
				navigate('/access-management')
			}
		})
	}

	if (!access || isLoading || !isFormHydrated) return <LoadingSpinner />

	const existingDisplayPermissions = access.permissions.filter(
		(permission) => !isSystemManagedPermission(permission)
	)

	return (
		<>
			<Form
				actions={
					canModifyAccess || canDeleteAccess ? (
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={() => navigate(-1)}>
								Cancel
							</Button>
							{canDeleteAccess && (
								<Button
									variant="link"
									loading={deleteLoading}
									onClick={() => setDeleteModalVisible(true)}
								>
									Delete
								</Button>
							)}
							{canModifyAccess && (
								<Button
									variant="primary"
									loading={updateLoading}
									onClick={handleSaveClick}
								>
									Save
								</Button>
							)}
						</SpaceBetween>
					) : (
						<Button variant="link" onClick={() => navigate(-1)}>
							Back
						</Button>
					)
				}
				header={
					<Header variant="h1" description="">
						{canModifyAccess ? 'Update/Edit Access' : 'User Access Details'}
					</Header>
				}
			>
				<Container>
					<SpaceBetween direction="vertical" size="l">
						<FormField label="Email">
							<Input disabled value={userEmail} />
						</FormField>

						<RaciRoleSelector
							key={`${userEmail}-${access.permissions.join('|')}`}
							control={control}
							setFormValues={setFormValues}
							raciRoles={formValues.raciRoles}
							customPermissions={formValues.customPermissions}
							existingPermissions={existingDisplayPermissions}
							hasExistingAccess={existingDisplayPermissions.length > 0}
							showCustomPermissions={isSuperAdmin(
								currentUserAccess?.permissions
							)}
							readOnly={!canModifyAccess}
						/>
					</SpaceBetween>
				</Container>
			</Form>

			<Modal
				visible={deleteModalVisible}
				onDismiss={() => !deleteLoading && setDeleteModalVisible(false)}
				header="Delete user access"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								variant="link"
								disabled={deleteLoading}
								onClick={() => setDeleteModalVisible(false)}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								loading={deleteLoading}
								onClick={handleAccessDelete}
							>
								Delete access
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				Remove all SSP access for <strong>{userEmail}</strong>? This action cannot
				be undone.
			</Modal>

			<Modal
				visible={saveModalVisible}
				onDismiss={() => !updateLoading && setSaveModalVisible(false)}
				header="Confirm access update"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								variant="link"
								disabled={updateLoading}
								onClick={() => setSaveModalVisible(false)}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								loading={updateLoading}
								onClick={handleAccessChange}
							>
								Save changes
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<SpaceBetween size="s">
					<Box>
						Save updated access for <strong>{userEmail}</strong>?
					</Box>
					{formValues.raciRoles.length > 0 && (
						<div
							style={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '6px'
							}}
						>
							{(formValues.raciRoles as Array<{ label: string; value: string }>).map(
								(role) => (
									<Badge key={role.value} color="green">
										{role.label}
									</Badge>
								)
							)}
						</div>
					)}
				</SpaceBetween>
			</Modal>
		</>
	)
}

export default AccessManagementDetails
